import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleProfileClick = () => {
    navigate(`/profile/`);
  };

  return (
    <header className='bg-gray-100 py-4 shadow-md border-b border-gray-200'>
      <div className='flex justify-between items-center px-4 sm:px-8'>
        {/* Left side - Logo and Navigation */}
        <div className='flex items-center gap-8'>
          <Link
            to={user ? '/practice' : '/'}
            className='flex items-center'
          >
            <img
              src='/logofull.png'
              alt='StrongHire'
              className='h-8 sm:h-10'
            />
          </Link>
          {!user && (
            <nav className='hidden md:flex gap-6'>
              <Link
                to='/practice'
                className='text-gray-700 font-medium hover:text-blue-600 transition-colors'
              >
                Practice
              </Link>
              <Link
                to='/pricing'
                className='text-gray-700 font-medium hover:text-blue-600 transition-colors'
              >
                Pricing
              </Link>
            </nav>
          )}
        </div>

        {/* Auth buttons - show on mobile when user is signed in */}
        <div className={user ? 'flex items-center gap-4' : 'hidden md:flex items-center gap-4'}>
          {loading ? (
            <div className='text-gray-700'>Loading...</div>
          ) : user ? (
            <button
              onClick={handleProfileClick}
              className='flex items-center gap-3 hover:opacity-80 transition-opacity'
            >
              <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center'>
                <span className='text-sm font-bold text-blue-600'>
                  {user.first_name?.charAt(0).toUpperCase() || 'U'}
                  {user.last_name?.charAt(0).toUpperCase() || 'N'}
                </span>
              </div>
              <span className='text-gray-700 font-medium'>
                {user.first_name}
              </span>
            </button>
          ) : (
            <>
              <Link
                to='/login'
                className='text-gray-700 font-medium hover:text-blue-600 transition-colors px-4 py-2'
              >
                Log In
              </Link>
              <Link
                to='/signup'
                className='bg-blue-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
              >
                Sign Up
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button - only show when user is not signed in */}
        {!user && (
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className='md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-200 transition-colors'
            aria-label='Toggle menu'
          >
          <svg
            className='w-6 h-6 text-gray-700'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            {isMenuOpen ? (
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            ) : (
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 6h16M4 12h16M4 18h16'
              />
            )}
          </svg>
          </button>
        )}
      </div>

      {/* Mobile menu overlay - only show when user is not signed in */}
      {!user && isMenuOpen && (
        <div
          className='fixed inset-0 bg-black bg-opacity-50 md:hidden z-[1]'
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile menu - only show when user is not signed in */}
      {!user && (
        <div
          className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden z-[1] ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
        <div className='p-6'>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-lg font-semibold text-gray-800'> </h2>
            <button
              onClick={() => setIsMenuOpen(false)}
              className='flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors'
            >
              <svg
                className='w-5 h-5 text-gray-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>

          <div className='flex flex-col space-y-4'>
            <Link
              to='/'
              className='text-gray-700 font-medium hover:text-blue-600 transition-colors py-3 border-b border-gray-100'
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            {!user && (
              <>
                <Link
                  to='/practice'
                  className='text-gray-700 font-medium hover:text-blue-600 transition-colors py-3 border-b border-gray-100'
                  onClick={() => setIsMenuOpen(false)}
                >
                  Practice
                </Link>
                <Link
                  to='/pricing'
                  className='text-gray-700 font-medium hover:text-blue-600 transition-colors py-3 border-b border-gray-100'
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pricing
                </Link>
              </>
            )}

            <div className='flex flex-col space-y-4 mt-4'>
              <Link
                to='/login'
                className='text-gray-700 font-medium hover:text-blue-600 transition-colors py-3 text-center border border-gray-300 rounded-lg'
                onClick={() => setIsMenuOpen(false)}
              >
                Log In
              </Link>
              <Link
                to='/signup'
                className='bg-blue-600 text-white font-medium px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors text-center'
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
        </div>
      )}
    </header>
  );
};

export default Header;
